
import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toInternalPath } from "@/utils/toInternalPath";

interface PublicRouteProps {
  children: ReactNode;
}

const PublicRoute = ({ children }: PublicRouteProps) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();
  
  // Get the intended destination from query parameters.
  //
  // `returnUrl` is attacker-controlled, and this component redirects to it BEFORE
  // the wrapped page renders — so for an already-authenticated visitor, LoginPage's
  // own toInternalPath call never runs. Guarding only there left the sanitisation
  // bypassable on the very route it was added to protect: /login?returnUrl=//evil.com
  // reaches history.pushState, which throws SecurityError cross-origin, and
  // react-router's catch falls through to window.location.assign(url) — a full
  // off-origin redirect. See toInternalPath.ts for the normalisation rules.
  //
  // Only the ABSENT case falls back to /dashboard. A present-but-rejected value collapses
  // to "/", same as LoginPage.tsx:36 — do NOT remap "/" to "/dashboard" here. Index.tsx:29
  // emits `/login?returnUrl=%2F` (the app's only returnUrl producer), and "/" is role-aware:
  // Index routes admins to /dashboard and camp_manager/camp_contributor to /welcome. Sending
  // "/" to /dashboard instead would drop every non-admin on RoleProtectedRoute's FullPageError,
  // whose only button signs them out. It would also make PublicRoute and LoginPage disagree on
  // the same input while both are mounted on /login, so the post-login destination would come
  // down to which one wins the race.
  const searchParams = new URLSearchParams(location.search);
  const rawReturnUrl = searchParams.get("returnUrl");
  const returnUrl = rawReturnUrl ? toInternalPath(rawReturnUrl) : "/dashboard";
  
  if (loading) {
    // Show nothing while auth state is being determined
    return null;
  }
  
  // If user is already authenticated and tries to access a public route
  // redirect them to the returnUrl or dashboard
  // BUT: Allow anonymous users (guest users) to access login page
  if (currentUser && !currentUser.isAnonymous) {
    return <Navigate to={returnUrl} replace />;
  }

  return <>{children}</>;
};

export default PublicRoute;
